<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
                >Search Form
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="loadReport">
                  <v-row class="px-2 py-2">
                    <v-col lg="4" md="4" cols="12">
                      <v-autocomplete
                        v-model="filters.lc_id"
                        :items="lcList"
                        :item-title="item => `${item.lc_no}${item.code ? ' - ' + item.code : ''}`"
                        item-value="id"
                        label="Filter by LC (Optional)"
                        density="compact"
                        prepend-inner-icon="mdi-file-document-outline"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        clearable
                        hide-details
                      ></v-autocomplete>
                    </v-col>

                    <v-col lg="2" md="2" cols="12">
                      <v-btn
                        class="bg-brand w-15"
                        type="submit"
                        :loading="loading"
                        prepend-icon="mdi-magnify"
                        >Search</v-btn
                      >
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <v-container fluid style="margin-top: -20px;">
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel1">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                LC-GRN Report (LC vs Received Goods)
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        v-if="reportData.length > 0"
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV"
                          >CSV</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel"
                          >EXCEL</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                          >PDF</v-btn
                        >
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12">
                    <div class="text-right">
                      <v-chip
                        v-if="reportData.length > 0"
                        color="primary"
                        variant="tonal"
                        size="small"
                      >
                        Total LCs: {{ reportData.length }}
                      </v-chip>
                    </div>
                  </v-col>
                </v-row>

                <!-- LC List Table -->
                <v-data-table
                  :headers="lcHeaders"
                  :items="filteredData"
                  :search="search"
                  :loading="loading"
                  item-value="id"
                  class="elevation-1"
                  density="compact"
                  show-expand
                  v-model:expanded="expandedLcRows"
                >
                  <template v-slot:expander="{ item, expanded }">
                    <v-btn
                      :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                      size="small"
                      variant="text"
                      color="primary"
                      @click.stop="toggleLcExpand(item)"
                    ></v-btn>
                  </template>

                  <template v-slot:item.index="{ index }">
                    {{ index + 1 }}
                  </template>

                  <template v-slot:item.lc_no="{ item }">
                    <strong>{{ item.lc_no }}</strong>
                  </template>

                  <template v-slot:item.code="{ item }">
                    {{ item.code || "-" }}
                  </template>

                  <template v-slot:item.supplier="{ item }">
                    {{ item.supplier?.supplier_name || "-" }}
                  </template>

                  <template v-slot:item.bank="{ item }">
                    {{ item.bank?.name || "-" }}
                  </template>

                  <template v-slot:item.lc_amount="{ item }">
                    {{ $numberFormat(item.lc_amount) }}
                  </template>

                  <template v-slot:item.grn_count="{ item }">
                    {{ item.gnr_masters?.length || 0 }}
                  </template>

                  <template v-slot:item.pi_count="{ item }">
                    {{ item.pis?.length || 0 }}
                  </template>

                  <template v-slot:expanded-row="{ item, columns }">
                    <tr>
                      <td :colspan="columns.length" class="pa-4 bg-grey-lighten-4">
                        <v-row>
                          <!-- GRN Section -->
                          <v-col cols="12" md="6">
                            <div class="mb-4">
                              <h3 class="text-h6 mb-2">GRN Records ({{ item.gnr_masters?.length || 0 }})</h3>
                              <div v-if="!item.gnr_masters || item.gnr_masters.length === 0" class="text-center text-grey pa-4">
                                No GRN records found for this LC
                              </div>
                              <div v-else>
                                <v-data-table
                                  :headers="grnHeaders"
                                  :items="item.gnr_masters"
                                  item-value="id"
                                  class="elevation-1"
                                  density="compact"
                                  show-expand
                                  v-model:expanded="expandedGrnRows"
                                >
                                  <template v-slot:expander="{ item: grnItem, expanded }">
                                    <v-btn
                                      :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                                      size="small"
                                      variant="text"
                                      color="info"
                                      @click.stop="toggleGrnExpand(grnItem)"
                                    ></v-btn>
                                  </template>

                                  <template v-slot:item.index="{ index }">
                                    {{ index + 1 }}
                                  </template>

                                  <template v-slot:item.gnr_no="{ item }">
                                    <strong>{{ item.gnr_no || "-" }}</strong>
                                  </template>

                                  <template v-slot:item.gnr_date="{ item }">
                                    {{ formatDate(item.gnr_date) }}
                                  </template>

                                  <template v-slot:item.approval_status="{ item }">
                                    <v-chip
                                      :color="getApprovalStatusColor(item.approval_status)"
                                      size="small"
                                      variant="tonal"
                                    >
                                      {{ item.approval_status || "Pending" }}
                                    </v-chip>
                                  </template>

                                  <template v-slot:item.details_count="{ item }">
                                    {{ item.gnr_details?.length || 0 }}
                                  </template>

                                  <template v-slot:expanded-row="{ item: grnItem, columns: grnColumns }">
                                    <tr>
                                      <td :colspan="grnColumns.length" class="pa-4 bg-grey-lighten-5">
                                        <v-table density="compact" class="elevation-1">
                                          <thead>
                                            <tr>
                                              <th>#</th>
                                              <th>Product</th>
                                              <th class="text-right">Received Qty</th>
                                              <th class="text-right">Accepted Qty</th>
                                              <th class="text-right">Rejected Qty</th>
                                              <th class="text-right">Inv Received Qty</th>
                                              <th class="text-right">Rate</th>
                                              <th class="text-right">Amount</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            <tr v-for="(detail, detailIndex) in (grnItem.gnr_details || [])" :key="detail.id">
                                              <td>{{ detailIndex + 1 }}</td>
                                              <td>
                                                <span v-if="detail.product">{{ detail.product.name }} ({{ detail.product.code }})</span>
                                                <span v-else class="text-grey">Product ID: {{ detail.product_id }}</span>
                                              </td>
                                              <td class="text-right">{{ formatNumber(detail.received_qty) }}</td>
                                              <td class="text-right">{{ formatNumber(detail.accepted_qty) }}</td>
                                              <td class="text-right">{{ formatNumber(detail.rejected_qty) }}</td>
                                              <td class="text-right">{{ formatNumber(detail.inv_received_qty) }}</td>
                                              <td class="text-right">{{ formatNumber(detail.rate) }}</td>
                                              <td class="text-right">{{ formatNumber(detail.amount) }}</td>
                                            </tr>
                                            <tr v-if="!grnItem.gnr_details || grnItem.gnr_details.length === 0">
                                              <td colspan="8" class="text-center text-grey">No items found</td>
                                            </tr>
                                          </tbody>
                                        </v-table>
                                      </td>
                                    </tr>
                                  </template>
                                </v-data-table>
                              </div>
                            </div>
                          </v-col>

                          <!-- PI Section -->
                          <v-col cols="12" md="6">
                            <div class="mb-4">
                              <h3 class="text-h6 mb-2">PI Records ({{ item.pis?.length || 0 }})</h3>
                              <div v-if="!item.pis || item.pis.length === 0" class="text-center text-grey pa-4">
                                No PI records found for this LC
                              </div>
                              <div v-else>
                                <v-data-table
                                  :headers="piHeaders"
                                  :items="item.pis"
                                  item-value="id"
                                  class="elevation-1"
                                  density="compact"
                                  show-expand
                                  v-model:expanded="expandedPiRows"
                                >
                                  <template v-slot:expander="{ item: piItem, expanded }">
                                    <v-btn
                                      :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                                      size="small"
                                      variant="text"
                                      color="success"
                                      @click.stop="togglePiExpand(piItem)"
                                    ></v-btn>
                                  </template>

                                  <template v-slot:item.index="{ index }">
                                    {{ index + 1 }}
                                  </template>

                                  <template v-slot:item.pi_no="{ item }">
                                    <strong>{{ item.pi_no }}</strong>
                                  </template>

                                  <template v-slot:item.total_amount="{ item }">
                                    {{ $numberFormat(item.total_amount) }}
                                  </template>

                                  <template v-slot:expanded-row="{ item: piItem, columns: piColumns }">
                                    <tr>
                                      <td :colspan="piColumns.length" class="pa-4 bg-grey-lighten-5">
                                        <v-table density="compact" class="elevation-1">
                                          <thead>
                                            <tr>
                                              <th>#</th>
                                              <th>Product</th>
                                              <th class="text-right">Quantity</th>
                                              <th class="text-right">Rate</th>
                                              <th class="text-right">Amount</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            <tr v-for="(detail, detailIndex) in (piItem.details || [])" :key="detail.id">
                                              <td>{{ detailIndex + 1 }}</td>
                                              <td>
                                                <span v-if="detail.product">{{ detail.product.name }} ({{ detail.product.code }})</span>
                                                <span v-else class="text-grey">Product ID: {{ detail.product_id }}</span>
                                              </td>
                                              <td class="text-right">{{ formatNumber(detail.qty) }}</td>
                                              <td class="text-right">{{ formatNumber(detail.rate) }}</td>
                                              <td class="text-right">{{ formatNumber(detail.amount) }}</td>
                                            </tr>
                                            <tr v-if="!piItem.details || piItem.details.length === 0">
                                              <td colspan="5" class="text-center text-grey">No items found</td>
                                            </tr>
                                          </tbody>
                                        </v-table>
                                      </td>
                                    </tr>
                                  </template>
                                </v-data-table>
                              </div>
                            </div>
                          </v-col>
                        </v-row>
                      </td>
                    </tr>
                  </template>

                  <template v-slot:no-data>
                    <div class="text-center pa-4">
                      <v-icon size="48" color="grey">mdi-database-search</v-icon>
                      <p class="text-grey mt-2">No LC data found</p>
                      <p class="text-caption text-grey">Click Search to load report data</p>
                    </div>
                  </template>
                </v-data-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import formatDateView from "@/plugins/dateFormatView";
import { ref, computed, onMounted } from "vue";
import { toast } from "vue3-toastify";
import { useStore } from "vuex";
import Axios from "axios";
import { getLcImportGrnReport } from "@/store/api";

export default {
  name: "LcImportGrnReport",
  setup() {
    const store = useStore();

    const panel = ref([0]);
    const panel1 = ref([0]);
    const loading = ref(false);
    const search = ref("");
    const reportData = ref([]);
    const filters = ref({
      lc_id: null,
    });

    const expandedLcRows = ref([]);
    const expandedGrnRows = ref([]);
    const expandedPiRows = ref([]);

    const lcHeaders = [
      { title: "#SL", align: "center", key: "index", width: "5%", sortable: false },
      { title: "LC No", key: "lc_no", align: "left", sortable: true },
      { title: "LC Code", key: "code", align: "left", sortable: true },
      { title: "Supplier", key: "supplier", align: "left", sortable: false },
      { title: "Bank", key: "bank", align: "left", sortable: false },
      { title: "LC Amount", key: "lc_amount", align: "right", sortable: true },
      { title: "GRN Count", key: "grn_count", align: "center", sortable: false },
      { title: "PI Count", key: "pi_count", align: "center", sortable: false },
      { title: "Actions", align: "center", key: "data-table-expand", sortable: false, width: "10%" },
    ];

    const grnHeaders = [
      { title: "#SL", align: "center", key: "index", width: "5%", sortable: false },
      { title: "GRN No", key: "gnr_no", align: "left", sortable: true },
      { title: "GRN Date", key: "gnr_date", align: "left", sortable: true },
      { title: "Code", key: "code", align: "left", sortable: true },
      { title: "Status", key: "approval_status", align: "center", sortable: true },
      { title: "Items", key: "details_count", align: "center", sortable: false },
      { title: "Actions", align: "center", key: "data-table-expand", sortable: false, width: "10%" },
    ];

    const piHeaders = [
      { title: "#SL", align: "center", key: "index", width: "5%", sortable: false },
      { title: "PI No", key: "pi_no", align: "left", sortable: true },
      { title: "Total Amount", key: "total_amount", align: "right", sortable: true },
      { title: "Actions", align: "center", key: "data-table-expand", sortable: false, width: "10%" },
    ];

    const lcList = computed(() => {
      return store.getters.getLcImportLcMasterList || [];
    });

    const filteredData = computed(() => {
      if (!search.value) return reportData.value;
      
      const searchLower = search.value.toLowerCase();
      return reportData.value.filter(lc => {
        const lcMatch = 
          lc.lc_no?.toLowerCase().includes(searchLower) ||
          lc.code?.toLowerCase().includes(searchLower) ||
          lc.supplier?.supplier_name?.toLowerCase().includes(searchLower) ||
          lc.bank?.name?.toLowerCase().includes(searchLower);
        
        const grnMatch = lc.gnr_masters?.some(grn => 
          grn.gnr_no?.toLowerCase().includes(searchLower) ||
          grn.code?.toLowerCase().includes(searchLower)
        );
        
        const piMatch = lc.pis?.some(pi => 
          pi.pi_no?.toLowerCase().includes(searchLower)
        );
        
        return lcMatch || grnMatch || piMatch;
      });
    });

    const toggleLcExpand = (lc) => {
      const index = expandedLcRows.value.findIndex(item => item.id === lc.id);
      if (index > -1) {
        expandedLcRows.value.splice(index, 1);
      } else {
        expandedLcRows.value.push(lc);
      }
    };

    const toggleGrnExpand = (grn) => {
      const index = expandedGrnRows.value.findIndex(item => item.id === grn.id);
      if (index > -1) {
        expandedGrnRows.value.splice(index, 1);
      } else {
        expandedGrnRows.value.push(grn);
      }
    };

    const togglePiExpand = (pi) => {
      const index = expandedPiRows.value.findIndex(item => item.id === pi.id);
      if (index > -1) {
        expandedPiRows.value.splice(index, 1);
      } else {
        expandedPiRows.value.push(pi);
      }
    };

    const loadReport = async () => {
      try {
        loading.value = true;
        const params = {};
        if (filters.value.lc_id) {
          params.lc_id = filters.value.lc_id;
        }

        const response = await Axios.get(getLcImportGrnReport, { params });
        reportData.value = response.data || [];
        
        if (reportData.value.length === 0) {
          toast.info("No LC-GRN data found for the selected criteria");
        } else {
          toast.success(`Loaded ${reportData.value.length} LC record(s)`);
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load LC-GRN report");
        reportData.value = [];
      } finally {
        loading.value = false;
      }
    };

    const formatNumber = (num) => {
      if (!num && num !== 0) return "0.00";
      return Number(num).toFixed(2);
    };

    const formatDate = (date) => {
      if (!date) return "";
      return formatDateView(date);
    };

    const getApprovalStatusColor = (status) => {
      const colors = {
        Pending: "warning",
        Approved: "success",
        Cancelled: "error",
      };
      return colors[status] || "default";
    };

    const exportToCSV = () => {
      toast.info("CSV export feature coming soon");
    };

    const exportToExcel = () => {
      toast.info("Excel export feature coming soon");
    };

    const exportToPDF = () => {
      toast.info("PDF export feature coming soon");
    };

    onMounted(async () => {
      // Load LC list for filter dropdown
      if (store.getters.getLcImportLcMasterList.length === 0) {
        try {
          await store.dispatch("getLcImportLcMasterList");
        } catch (error) {
          console.error("Failed to load LC list:", error);
        }
      }
    });

    return {
      panel,
      panel1,
      loading,
      search,
      reportData,
      filters,
      lcList,
      filteredData,
      lcHeaders,
      grnHeaders,
      piHeaders,
      expandedLcRows,
      expandedGrnRows,
      expandedPiRows,
      toggleLcExpand,
      toggleGrnExpand,
      togglePiExpand,
      loadReport,
      formatNumber,
      formatDate,
      getApprovalStatusColor,
      exportToCSV,
      exportToExcel,
      exportToPDF,
    };
  },
};
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>

